import { Button } from '@/components/ui/button'
import React, { useEffect, useState } from 'react'
import { WatchingMen } from '@/assets/builder/screens/WatchingMen'
import { useSelector, useDispatch } from 'react-redux'
import { useAppSelector } from '@/store'
import { selectHasStore, selectIsSuperAdmin, selectIsImpersonating } from '@/store/slices/authSlice'
import ConnectStoreModal from '@/components/common/ConnectStoreModal'

import Radio from '@/features/survey-builder/components/preview-modal/Radio'
import MultiSelect from '@/features/survey-builder/components/preview-modal/MultiSelect'
import {
    addPreviewQuestionPath,
    selectPreviewQuestion,
    clearPreviewResponses,
    resetQuestionPath,
} from '@/store/slices/preview'
import Text from '@/features/survey-builder/components/preview-modal/Text'
import { executeRuleEngine } from '@/utils/newRuleEngine'
import RatingScale from '@/features/survey-builder/components/preview-modal/RatingScale'
import Dropdown from '@/features/survey-builder/components/preview-modal/Dropdown'
import Date from '@/features/survey-builder/components/preview-modal/Date'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
} from '@/components/ui/dialog'
import { categoryTagColors } from '@/utils/utils'
import { Clock, HelpCircle, PencilIcon } from 'lucide-react'
import {
    CreateSurveyButton,
    SecondaryButton,
} from '@/features/survey-builder/Tabs/CustomButton'
import { useNavigate } from 'react-router-dom'
import { setCreateModal } from '@/store/slices/surveySlice'
import { FeatureAccess } from '../FeatureAccess'
import { FEATURE_IDS } from '@/config/features'
import SingleSelectImage from '@/features/survey-builder/components/preview-modal/SingleSelectImage'
import NpsScale from '@/features/survey-builder/components/preview-modal/NpsScale'
import ContentBlock from '@/features/survey-builder/components/preview-modal/ContentBlock'
import { usePermission } from '@/hooks/usePermission'
import { PERMISSIONS } from '@/features/auth/constants'

interface PreviewProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    survey: any
    needsStoreGate?: boolean
    onStoreRequired?: () => void
}

const NoQuestion = () => {
    return (
        <div className="w-full h-[420px] flex flex-col gap-3 justify-center items-center">
            <WatchingMen />
            <span className="text-[#616161]">Add questions to preview..</span>
        </div>
    )
}

const ThankYou = () => {
    const dispatch = useDispatch()
    const { questions } = useSelector((state: any) => state.preview)
    const [countdown, setCountdown] = React.useState(3)

    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer)
                    // Reset the survey state
                    dispatch(selectPreviewQuestion(questions[0]?.questionId))
                    dispatch(clearPreviewResponses())
                    dispatch(resetQuestionPath())
                    return 0
                }
                return prev - 1
            })
        }, 1000)

        return () => clearInterval(timer)
    }, [dispatch, questions])

    return (
        <div className="w-full h-[220px] flex flex-col gap-3 justify-center items-center">
            <div className="text-[#015AD1] text-4xl mb-4">✓</div>
            <h2 className="text-xl font-semibold text-[#015AD1]">Thank You!</h2>
            <span className="text-[#616161] text-center">
                Your responses have been submitted successfully.
            </span>
            <span className="text-[#616161] text-sm mt-2">
                Resetting in {countdown} seconds...
            </span>
        </div>
    )
}

const Preview: React.FC<PreviewProps> = ({ open, onOpenChange, survey, needsStoreGate: externalNeedsStoreGate, onStoreRequired }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { selectedQuestionId, questions, previewResponses, questionPath } =
        useSelector((state: any) => state.preview)
    const canEditTemplate = usePermission(PERMISSIONS.TEMPLATES_WRITE)
    const hasStore = useAppSelector(selectHasStore)
    const isSuperAdmin = useAppSelector(selectIsSuperAdmin)
    const isImpersonating = useAppSelector(selectIsImpersonating)
    const internalNeedsStoreGate = !hasStore && !isSuperAdmin && !isImpersonating
    const [showStoreRequired, setShowStoreRequired] = useState(false)
    
    // Use external gate if provided, otherwise fallback to internal logic
    const needsStoreGate = externalNeedsStoreGate !== undefined ? externalNeedsStoreGate : internalNeedsStoreGate

    // Get survey rules from builder state or fallback to survey prop
    const { surveyRules: builderRules } = useSelector(
        (state: any) => state.builder
    )
    const surveyRules = React.useMemo(() => {
        // Priority: 1. Builder rules (for active surveys being built)
        if (builderRules && builderRules.length > 0) {
            return builderRules
        }
        // Priority: 2. Survey prop rules (for templates or saved surveys)
        if (survey?.rules && Array.isArray(survey.rules)) {
            return survey.rules
        }
        // Priority: 3. Survey prop surveyRules (alternative property name)
        if (survey?.surveyRules && Array.isArray(survey.surveyRules)) {
            return survey.surveyRules
        }
        // Fallback: Empty array (no rules)
        return []
    }, [builderRules, survey])

    const currentQuestion = questions.find(
        (q: any) => q.questionId === selectedQuestionId
    )

    // Get current question index
    const currentIndex = questions.findIndex(
        (q: any) => q.questionId === selectedQuestionId
    )

    // Add this new state to track terminal state
    const [isTerminalState, setIsTerminalState] = React.useState(false)

    useEffect(() => {
        setIsTerminalState(false)
    }, [currentQuestion])

    // Handle navigation between questions
    const handleNext = () => {
        if (!currentQuestion) return

        let selectedAnswer

        // Get the appropriate answer based on question type
        if (
            currentQuestion.type === 'single_select' ||
            currentQuestion.type === 'image'
        ) {
            selectedAnswer =
                previewResponses[currentQuestion.questionId]?.optionId
        } else if (currentQuestion.type === 'multi_select') {
            selectedAnswer =
                previewResponses[currentQuestion.questionId]?.optionIds
        } else if (
            currentQuestion.type === 'rating' ||
            currentQuestion.type === 'nps'
        ) {
            selectedAnswer = previewResponses[currentQuestion.questionId]?.value
        } else if (currentQuestion.type === 'text') {
            selectedAnswer = previewResponses[currentQuestion.questionId]?.value
        } else {
            selectedAnswer = null
        }

        // Get the next question using the new rule engine via surveyNavigation
        const questionObj: Record<string, any> = {}
        for (const question of questions) {
            questionObj[question.questionId] = question
        }
        const questionSequence = questions.map((q: any) => q.questionId)

        console.log('[PREVIEW DEBUG] Navigation input:', {
            currentQuestionId: currentQuestion.questionId,
            questionSequence,
            questionsCount: Object.keys(questionObj).length,
            previewResponses,
            surveyRulesCount: surveyRules?.length || 0,
            surveyRules: surveyRules,
            sampleQuestion: questions[0], // To see question format
            builderRulesFromState: builderRules,
            surveyPropRules: survey?.rules,
            surveyPropSurveyRules: survey?.surveyRules,
            selectedAnswer: selectedAnswer,
        })

        // Convert preview responses to simple format for rule engine
        const simpleAnswers: Record<string, any> = {}
        Object.entries(previewResponses).forEach(
            ([questionId, response]: [string, any]) => {
                if (response.optionId) {
                    simpleAnswers[questionId] = response.optionId
                } else if (response.optionIds) {
                    simpleAnswers[questionId] = response.optionIds
                } else if (response.value !== undefined) {
                    simpleAnswers[questionId] = response.value
                }
            }
        )

        // Use executeRuleEngine directly
        const {
            nextQuestionId,
            isTerminal,
            hiddenQuestions,
            shownQuestions,
            appliedRules,
        } = executeRuleEngine(
            surveyRules || [],
            simpleAnswers,
            currentQuestion.questionId,
            questionSequence,
            questionObj
        )

        console.log('[PREVIEW DEBUG] Direct rule engine result:', {
            nextQuestionId,
            isTerminal,
            hiddenQuestions: Array.from(hiddenQuestions),
            shownQuestions: Array.from(shownQuestions),
            appliedRules,
            currentIndex: currentIndex,
            nextIndexInSequence: currentIndex + 1,
            nextQuestionInSequence:
                questions[currentIndex + 1]?.questionId || 'END',
        })

        // If we have a next question, navigate to it
        if (nextQuestionId) {
            dispatch(
                addPreviewQuestionPath({
                    questionId: nextQuestionId,
                    fromQuestionId: currentQuestion.questionId,
                })
            )
            dispatch(selectPreviewQuestion(nextQuestionId))
            setIsTerminalState(false) // Reset terminal state when navigating
        } else if (isTerminal) {
            // Set terminal state to true to show thank you screen
            setIsTerminalState(true)
            console.log('Survey completed')
        }
    }
    const handleBack = () => {
        if (currentIndex > 0) {
            // Check if currentQuestion or questionId is null
            if (!currentQuestion || !questionPath[currentQuestion.questionId]) {
                // If null, navigate to the previous question using index
                const previousQuestionId =
                    questions[currentIndex - 1]?.questionId
                if (previousQuestionId) {
                    dispatch(selectPreviewQuestion(previousQuestionId))
                }
            } else {
                // Use the question path if available
                dispatch(
                    selectPreviewQuestion(
                        questionPath[currentQuestion.questionId]
                    )
                )
            }
        }
    }

    return (
        <>
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[550px]">
                <DialogHeader>
                    <div
                        className="inline-block w-fit px-[8px] py-[2px] bg-amber-100 text-amber-800 text-xs font-medium rounded-md"
                        style={{
                            backgroundColor: categoryTagColors(
                                survey.categoryTag
                            ).bg,
                            color: categoryTagColors(survey.categoryTag).text,
                        }}
                    >
                        {survey.categoryTag}
                    </div>
                    <h4 className="text-[16px] truncate ml-1 mb-2 font-[650] text-[#303030]">
                        {survey.name}
                    </h4>
                    <div className="flex items-center gap-2">
                        <HelpCircle className="h-4 w-4 ml-1" stroke="#616161" />
                        <span className="text-[12px] mr-2 font-[450] text-[#616161]">
                            {survey.questionsCount} questions
                        </span>
                        <Clock className="h-4 w-4" stroke="#616161" />
                        <span className="text-[12px] font-[450] text-[#616161]">
                            {survey.estimatedTimeBreakdown.minutes} mins
                        </span>
                    </div>
                </DialogHeader>
                <div className="rounded-lg flex justify-center items-center p-4 bg-[#F5F5F5] h-[50vh]">
                    <div className="w-[470px] h-full relative flex flex-col w-full overflow-y-auto overflow-x-hidden overflow-scroll custom-scrollbar">
                        <div className="mt-[2px] w-full px-5 pt-8 pb-10 bg-[#FFFFFF] rounded-xl border border-[1px] border-[#DEDEDE]">
                            {!selectedQuestionId ? (
                                <NoQuestion />
                            ) : isTerminalState ? (
                                <ThankYou />
                            ) : (
                                <>
                                    <div className="flex items-center gap-2 p">
                                        {currentQuestion?.text !== '' ? (
                                            <span className="text-[16px] font-[550]">
                                                {currentQuestion?.text}
                                            </span>
                                        ) : (
                                            <span className="text-[16px] font-[450] text-[#616161]">
                                                Your question text comes here..
                                            </span>
                                        )}
                                    </div>
                                    {currentQuestion?.description && (
                                        <div className="text-[13px] font-[450] text-[#616161] mt-2">
                                            {currentQuestion?.description}
                                        </div>
                                    )}
                                    {currentQuestion?.inlineImage &&
                                        currentQuestion?.inlineImage
                                            ?.placement === 'below' && (
                                            <div className="flex items-center justify-start gap-2 max-w-[430px] max-h-[220px] my-4">
                                                <img
                                                    src={
                                                        currentQuestion
                                                            ?.inlineImage
                                                            ?.imageUrl
                                                    }
                                                    alt={
                                                        currentQuestion
                                                            ?.inlineImage
                                                            ?.altText
                                                    }
                                                    className="max-w-[430px] max-h-[220px] w-auto h-auto object-contain rounded-md"
                                                />
                                            </div>
                                        )}
                                    <div className="">
                                        {currentQuestion?.type ===
                                            'single_select' ? (
                                            <Radio />
                                        ) : currentQuestion?.type ===
                                            'multi_select' ? (
                                            <MultiSelect />
                                        ) : currentQuestion?.type === 'text' ? (
                                            <Text />
                                        ) : currentQuestion?.type ===
                                            'rating' ? (
                                            <RatingScale />
                                        ) : currentQuestion?.type === 'nps' ? (
                                            <NpsScale />
                                        ) : currentQuestion?.type ===
                                            'drop_down' ? (
                                            <Dropdown />
                                        ) : currentQuestion?.type === 'date' ? (
                                            <Date />
                                        ) : currentQuestion?.type ===
                                            'image' ? (
                                            <SingleSelectImage />
                                        ) : currentQuestion?.type ===
                                            'content_block' ? (
                                            <ContentBlock />
                                        ) : null}
                                    </div>

                                    {/* Navigation buttons */}
                                    {selectedQuestionId &&
                                        questions.length > 0 && (
                                            <div className="flex justify-between mt-8">
                                                <Button
                                                    onClick={handleBack}
                                                    variant="outline"
                                                    className={`${currentIndex > 0
                                                        ? 'text-[#616161] hover:bg-[#EBEBEB] text-[#015AD1] border-[#DEDEDE]'
                                                        : 'invisible'
                                                        }`}
                                                >
                                                    Back
                                                </Button>
                                                <Button
                                                    onClick={handleNext}
                                                    className={`${'bg-[#015AD1] text-white hover:bg-[#015AD1]'}`}
                                                >
                                                    {currentIndex <
                                                        questions.length - 1
                                                        ? 'Next'
                                                        : 'Submit'}
                                                </Button>
                                            </div>
                                        )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <DialogFooter className="w-full">
                    <div className="w-full flex items-center justify-between gap-2">
                        <SecondaryButton
                            text="Back"
                            onClick={() => onOpenChange(false)}
                        />
                        <div className="flex items-center gap-2">
                            {isSuperAdmin && (
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => {
                                        dispatch(setCreateModal(null))
                                        navigate(
                                            `/builder/${survey.surveyId}?isTemplate=true`
                                        )
                                    }}
                                >
                                    <PencilIcon className="h-4 w-4" />
                                </Button>
                            )}
                            <FeatureAccess
                                featureId={FEATURE_IDS.QUESTIONS_LIMIT}
                                fallback={
                                    <CreateSurveyButton
                                        text="Use Template"
                                        onClick={() => {
                                            if (needsStoreGate) {
                                                if (onStoreRequired) {
                                                    onStoreRequired()
                                                } else {
                                                    onOpenChange(false)
                                                    setShowStoreRequired(true)
                                                }
                                            }
                                        }}
                                    />
                                }
                                currentLimit={survey?.questionsCount - 1}
                            >
                                <CreateSurveyButton
                                    text="Use Template"
                                    onClick={() => {
                                        if (needsStoreGate) {
                                            if (onStoreRequired) {
                                                onStoreRequired()
                                            } else {
                                                onOpenChange(false)
                                                setShowStoreRequired(true)
                                            }
                                            return
                                        }
                                        dispatch(setCreateModal(null))
                                        navigate(
                                            `/surveys/template/${survey.surveyId}`
                                        )
                                    }}
                                />
                            </FeatureAccess>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        {!onStoreRequired && (
            <ConnectStoreModal
                open={showStoreRequired}
                onOpenChange={setShowStoreRequired}
            />
        )}
        </>
    )
}

export default Preview
